import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, BellOff, Calendar, CheckCircle, Clock, FileText, MessageSquare, Pill, RefreshCw, X } from "lucide-react";
import api from "@/api";

const getPatientLink = (notif) => {
  if (["appointment", "appointment_request"].includes(notif.type)) return "/patient/appointments";
  if (notif.type === "message") return "/patient/messages";
  if (notif.type === "document") return "/patient/medical-record";
  if (notif.type === "oral_adherence") return "/patient/home";
  return "/patient/home";
};

const getIcon = (type) => {
  if (["appointment", "appointment_request"].includes(type)) return <Calendar className="w-4 h-4 text-sky-600" />;
  if (type === "message") return <MessageSquare className="w-4 h-4 text-emerald-600" />;
  if (type === "document") return <FileText className="w-4 h-4 text-teal-600" />;
  if (type === "oral_adherence") return <Pill className="w-4 h-4 text-amber-600" />;
  return <Bell className="w-4 h-4 text-[#2C5E53]" />;
};

const timeAgo = (timestamp) => {
  const date = new Date(timestamp);
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return "À l'instant";
  if (minutes < 60) return `Il y a ${minutes} min`;
  if (hours < 24) return `Il y a ${hours}h`;
  if (days < 7) return `Il y a ${days}j`;
  return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
};

export default function PatientNotificationCenter({ initialCount = 0, onRead }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(initialCount);
  const ref = useRef(null);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await api.get("/notifications?limit=30");
      const items = res.data.notifications || [];
      setNotifications(items);
      setUnreadCount(res.data.unreadCount ?? items.filter((item) => !item.read).length);
    } catch {
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNotifications(); }, []);

  useEffect(() => { setUnreadCount(initialCount); }, [initialCount]);

  useEffect(() => {
    const onClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (id) => {
    setNotifications((prev) => prev.map((item) => item.id === id ? { ...item, read: true } : item));
    setUnreadCount((value) => Math.max(0, value - 1));
    try { await api.patch(`/notifications/${id}/read`); } catch { /* ignore */ }
    if (onRead) onRead();
  };

  const markAll = async () => {
    setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
    setUnreadCount(0);
    try {
      await api.patch("/notifications/mark-all-read");
      await api.post("/kpi/patient/badges/mark-viewed?section=notifications");
      if (onRead) onRead();
    } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((value) => !value)}
        data-testid="patient-notification-bell"
        className="relative p-1.5 text-slate-400 hover:text-[#2C5E53] hover:bg-[#E9F0EE] rounded-2xl transition-all duration-200"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span data-testid="patient-notification-badge" className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-extrabold flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="patient-notification-panel" className="absolute right-0 top-full mt-2 w-[min(22rem,calc(100vw-1rem))] bg-white rounded-3xl shadow-2xl border border-[#2C5E53]/10 z-50 overflow-hidden">
          <div className="px-4 py-3 bg-[#FCFBF8] border-b border-[#2C5E53]/10 flex items-center justify-between gap-2">
            <div>
              <p className="font-extrabold text-[#2C5E53]">Notifications</p>
              <p className="text-xs text-slate-400">Vos messages, RDV et rappels Hana</p>
            </div>
            <div className="flex items-center gap-1">
              {notifications.length > 0 && (
                <button data-testid="patient-notification-mark-all" onClick={markAll} className="text-xs font-bold text-[#2C5E53] px-2 py-1 rounded-xl hover:bg-[#E9F0EE]">
                  Tout lire
                </button>
              )}
              <button data-testid="patient-notification-close" onClick={() => setOpen(false)} className="p-1.5 rounded-xl hover:bg-[#E9F0EE]">
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 flex justify-center"><RefreshCw className="w-5 h-5 text-slate-300 animate-spin" /></div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <BellOff className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400">Aucune notification</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {notifications.map((notif) => {
                  const content = (
                    <div onClick={() => markRead(notif.id)} className={`p-4 flex items-start gap-3 hover:bg-[#F8FAF9] transition-colors ${!notif.read ? "bg-emerald-50/50" : ""}`}>
                      <div className="w-9 h-9 rounded-2xl bg-white border border-[#2C5E53]/10 flex items-center justify-center flex-shrink-0 shadow-sm">
                        {getIcon(notif.type)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-extrabold text-slate-800 truncate">{notif.title}</p>
                          {!notif.read && <span className="w-2 h-2 rounded-full bg-rose-500 flex-shrink-0" />}
                        </div>
                        <p className="text-sm text-slate-500 truncate">{notif.message}</p>
                        <p className="mt-1 text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />{timeAgo(notif.timestamp)}</p>
                      </div>
                    </div>
                  );
                  return (
                    <Link key={notif.id} to={getPatientLink(notif)} onClick={() => setOpen(false)} data-testid={`patient-notification-item-${notif.id}`}>
                      {content}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
          {notifications.length > 0 && (
            <div className="p-3 bg-[#FCFBF8] border-t border-[#2C5E53]/10">
              <button data-testid="patient-notification-refresh" onClick={fetchNotifications} className="w-full text-sm font-bold text-[#2C5E53] flex items-center justify-center gap-2">
                <RefreshCw className="w-4 h-4" /> Actualiser
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}